import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSignupDraft, updateSignupDraft } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    TextField,
    PrimaryButton,
    GhostButton,
} from "@/screens/auth/_ui";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
const isPhone = (v) => /^\+?[0-9]{7,15}$/.test(v.replace(/\s+/g, ""));

const SignupContact = () => {
    const navigate = useNavigate();
    const { identifierIsTaken } = useAuth();
    const [draft, setDraft] = useState(null);
    const [value, setValue] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        const d = getSignupDraft();
        if (!d?.fullName) {
            navigate("/auth/signup", { replace: true });
            return;
        }
        setDraft(d);
    }, [navigate]);

    if (!draft) return null;

    const primaryIsPhone = draft.method === "phone";
    const askFor = primaryIsPhone ? "email" : "phone"; // opposite of primary
    const label = askFor === "phone" ? "Phone number (optional)" : "Email (optional)";
    const placeholder = askFor === "phone" ? "+234 800 000 0000" : "you@example.com";

    const handleContinue = () => {
        const trimmed = value.trim();
        if (trimmed) {
            const valid = askFor === "phone" ? isPhone(trimmed) : isEmail(trimmed);
            if (!valid) {
                setError(
                    askFor === "phone" ? "Enter a valid phone number" : "Enter a valid email"
                );
                return;
            }
            if (identifierIsTaken(trimmed)) {
                setError(
                    askFor === "phone"
                        ? "This phone is already registered"
                        : "This email is already registered"
                );
                return;
            }
            const patch = askFor === "phone" ? { phone: trimmed } : { email: trimmed };
            updateSignupDraft(patch);
        }
        navigate("/auth/signup/avatar");
    };

    const handleSkip = () => {
        navigate("/auth/signup/avatar");
    };

    return (
        <AuthScreen testId="signup-contact-screen">
            <ScreenHeader title="Add contact" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        {askFor === "phone" ? "Add a phone number?" : "Add an email?"}
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        Optional — helps you recover your account if you lose access.
                    </p>
                </div>

                <div className="mt-8 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    <TextField
                        testId="signup-contact-input"
                        label={label}
                        placeholder={placeholder}
                        value={value}
                        onChange={(v) => {
                            setValue(v);
                            if (error) setError(null);
                        }}
                        type={askFor === "phone" ? "tel" : "email"}
                        inputMode={askFor === "phone" ? "tel" : "email"}
                        autoComplete={askFor === "phone" ? "tel" : "email"}
                        autoFocus
                        error={error}
                        onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                    />
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton testId="signup-contact-continue" onClick={handleContinue}>
                    Continue
                </PrimaryButton>
                <GhostButton testId="signup-contact-skip" onClick={handleSkip}>
                    Skip for now
                </GhostButton>
            </AuthFooter>
        </AuthScreen>
    );
};

export default SignupContact;
